<template>
  <form class="account-details row"
        @submit.prevent="submit">
    <div class="col-md-6">
      <h3>Step 3: About you</h3>
      <div class="row">
        <div class="col-sm-3">
          <div class="form-group">
            <label :for="$id('title')">
              Title:
            </label>

            <b-form-select :id="$id('title')"
                           v-model="account.detail.title"
                           autocomplete="honorific-prefix">
              <b-form-select-option :value="null">
              </b-form-select-option>
              <b-form-select-option value="Mr">
                Mr
              </b-form-select-option>
              <b-form-select-option value="Mrs">
                Mrs
              </b-form-select-option>
              <b-form-select-option value="Ms">
                Ms
              </b-form-select-option>
              <b-form-select-option value="Miss">
                Miss
              </b-form-select-option>
              <b-form-select-option value="Dr">
                Dr
              </b-form-select-option>
            </b-form-select>
          </div>
        </div>
        <div class="col-sm-9">
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group">
                <label :for="$id('firstname')">
                  First name:
                </label>

                <b-form-input :id="$id('firstname')"
                              v-model="account.detail.firstname"
                              autocomplete="given-name"
                              class="form-control"
                              type="text">
                </b-form-input>

                <span v-if="showValidation && !account.detail.firstname"
                      class="validation-error">
                  Required
                </span>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label :for="$id('lastname')">
                  Last name:
                </label>

                <b-form-input :id="$id('lastname')"
                              v-model="account.detail.lastname"
                              autocomplete="family-name"
                              class="form-control"
                              type="text">
                </b-form-input>

                <span v-if="showValidation && !account.detail.lastname"
                      class="validation-error">
                  Required
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="form-group">
        <label :for="$id('email')">
          Email:
        </label>

        <b-form-input :id="$id('email')"
                      v-model="account.detail.email"
                      autocomplete="email"
                      class="form-control"
                      type="email">
        </b-form-input>

        <span v-if="showValidation && !account.detail.email"
              class="validation-error">
          Please enter your email address
        </span>
      </div>
      <div v-if="askForJobTypeId"
           class="form-group">
        <label :for="$id('role')">
          Role:
        </label>

        <b-form-select :id="$id('role')"
                       v-model="account.detail.jobType">
          <b-form-select-option selected
                                value="0">
            - Please select -
          </b-form-select-option>
          <b-form-select-option v-for="(jobType, index) in jobTypes"
                                :key="index"
                                :value="{id: jobType.jobTypeId, text: jobType.jobType}">
            {{ jobType.jobType }}
          </b-form-select-option>
        </b-form-select>

        <span v-if="showValidation && account.detail.jobType.id ==='0'"
              class="validation-error">
          Please select your role
        </span>
      </div>
      <div v-if="askForJobTitle"
           class="form-group">
        <label :for="$id('jobTitle')">
          Job title:
        </label>

        <b-form-input :id="$id('jobTitle')"
                      v-model="account.detail.jobTitle"
                      class="form-control"
                      type="text">
        </b-form-input>

        <span v-if="showValidation && !account.detail.jobTitle"
              class="validation-error">
          Please enter your job title
        </span>
      </div>
      <div class="form-group">
        <label :for="$id('password')">
          Password:
        </label>

        <b-form-input :id="$id('password')"
                      v-model="account.detail.password"
                      autocomplete="new-password"
                      class="form-control"
                      type="password">
        </b-form-input>

        <span v-if="showValidation && !account.detail.password"
              class="validation-error">
          Please enter a password
        </span>

        <span v-else-if="showValidation && account.detail.password.length < passwordMinLength"
              class="validation-error">
          Your password must be at least {{ passwordMinLength }} characters long
        </span>

        <span v-else-if="showValidation && !passwordHasDigit"
              class="validation-error">
          Your password must contain at least 1 digit
        </span>
      </div>
      <div class="form-group">
        <label :for="$id('repeat-password')">
          Repeat password:
        </label>

        <b-form-input :id="$id('repeat-password')"
                      v-model="repeatPassword"
                      autocomplete="new-password"
                      class="form-control"
                      type="password">
        </b-form-input>

        <span v-if="passwordMatch"
              class="validation-error">
          Passwords do not match
        </span>
      </div>

      <div class="form-group">
        <b-form-checkbox :id="$id('marketing-opt-in')"
                         v-model="account.marketingOptIn">
          <slot name="marketing-label">
            Opt in to marketing
          </slot>
        </b-form-checkbox>
      </div>
    </div>

    <div v-if="showAdditionalDetails"
         class="col-md-6">
      <h3>Additional information required</h3>
      <MetaData v-model="account.metaData"
                :show-validation="showValidation">
      </MetaData>
    </div>

    <div class="col-12 registration-buttons">
      <button type="button"
              :disabled="saving"
              class="btn btn-accent-6 btn-rounded"
              @click="$emit('goBack')">
        Go back
      </button>
      <button :disabled="saving"
              class="btn btn-accent-6 btn-rounded">
        <template v-if="saving">
          <span class="visually-hidden">
            Creating account, please wait...
          </span>
          <font-awesome-icon icon="spinner"
                             spin></font-awesome-icon>
        </template>
        <template v-else>
          Register and log in
        </template>
      </button>
    </div>
  </form>
</template>

<script>
import { get } from "lodash";
import { jobTypes } from "~/Scripts/resources/registrationResource";
import MetaData from "~/Components/MetaData/MetaData.vue";
import { metaDataConfig } from "~/Scripts/resources/metaDataResource";
import { ValidationMixin } from "spiritweb.packages.web-account";

export default {

  components: {
    MetaData
  },

  mixins: [ValidationMixin],

  props: {
    /**
     * V-model data. NB this component only supports one way binding, data cannot be passed in
     */
    value: {
      type: Object,
      default: () => {}
    },

    /**
     * The user type that contains the configuration details for this registration
     */
    userType: {
      type: Object,
      default: () => {}
    },

    /**
     * The default email address to use
     */
    defaultEmail: {
      type: String,
      default: null
    },

    /**
     * The organisaton we are loading meta data for
     */
    orgId: {
      type: String,
      default: null
    },

    /**
     * When set to true the buttons indicate that the registration data is being saved
     */
    saving: {
      type: Boolean,
      default: false
    }
  },

  data () {
    const blankAccount = this.blankAccount();

    return {
      passwordMinLength: 10,
      account: {
        detail: blankAccount,
        metaData: [],
        marketingOptIn: false
      },
      repeatPassword: null,
      showValidation: false,
      jobTypes: []
    };
  },

  computed: {
    /**
     * True if we have a password and it matches the "repeat password" field
     */
    passwordMatch () {
      return this.account.detail.password
              && this.account.detail.password !== this.repeatPassword;
    },

    /**
     * Org Meta Data container ID
     */
    containerId () {
      return get( this.userType, "showOrgMetaDataContainerId", null );
    },

    /**
     * Whether or not we should show meta data
     */
    showAdditionalDetails () {
      const hasMetaData = get( this.account, "metaData.length", 0 );
      return hasMetaData && this.containerId;
    },

    /**
     * Whether we should ask the user to select a Job Type
     */
    askForJobTypeId () {
      return get( this.userType, "askForJobTypeId", null );
    },

    /**
     * Whether we should ask the user to enter a Job Title
     */
    askForJobTitle () {
      return get( this.userType, "askForJobTitle", null );
    },

    /**
     * Whether the account details are valid
     */
    isValid () {
      const jobTypeId = get( this.account, "detail.jobType.id", null );

      return this.account.detail.firstname
                && this.account.detail.lastname
                && this.account.detail.email
                && ( this.account.detail.jobTitle || !this.askForJobTitle )
                && ( jobTypeId || !this.askForJobTypeId )
                && this.account.detail.password
                && this.account.detail.password.length >= this.passwordMinLength
                && this.passwordHasDigit
                && this.account.detail.password === this.repeatPassword;
    },

    passwordHasDigit () {
      let result = false;

      if ( /\d/.test( this.account.detail.password ) ) {
        result = true;
      }

      return result;
    }
  },

  watch: {
    /**
     * Watch for changes to the accountData
     */
    account: {
      deep: true,
      handler ( val ) {
        this.$emit( "input", val );
      }
    },

    /**
     * Watch for changes to the default email address
     */
    defaultEmail ( val ) {
      this.account.detail.email = val;
    },

    /**
     * Watch for changes to the web user type parent ID,
     * we need to reload our list of job types when this changes
     */
    "userType.webUserTypeParentId": {
      handler ( val ) {
        jobTypes( val )
          .then( result => {
            this.jobTypes = result.data || [];
          } )
          .catch( () => {
            // Ignore errors
          } );
      }
    }
  },

  mounted () {
    this.applyRegexToEmailInputFields();
  },

  methods: {
    /**
     * Load meta data for a specific organisation
     *
     * @param {GUID} orgId The organisation to load meta data for
     */
    loadMetaData ( orgId ) {
      if ( this.containerId ) {
        // Load meta data configuration for the specificed container for an organisation
        metaDataConfig( 1, this.containerId, null, orgId, true )
          .then( ( result ) => {
            this.account.metaData = result.data;
          } )
          .catch( () => {
            this.account.metaData = [];
          } );
      } else {
        this.account.metaData = [];
      }
    },

    /**
     * The form has been submitted
     */
    submit () {
      this.showValidation = true;

      if ( this.isValid ) {
        this.$emit( "proceed" );
      }
    },

    /**
     * Create a blank account
     */
    blankAccount () {
      return {
        title: null,
        firstname: null,
        lastname: null,
        email: this.defaultEmail,
        jobType: { id: "0", text: "-- Please select --" },
        jobTitle: null,
        password: null
      };
    }
  }
};
</script>
